import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Calendar, ShieldCheck, ArrowLeft, Plus, Check, Heart, Maximize2, Upload, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPrice } from "@/components/PropertyCard";
import { useCompare } from "@/context/CompareContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";
import Lightbox from "@/components/Lightbox";
import MapView from "@/components/MapView";
import { buildWhatsAppLink } from "@/components/WhatsAppFab";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "amenities", label: "Amenities" },
  { id: "floorplans", label: "Floor Plans" },
  { id: "location", label: "Location" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const { add, remove, has } = useCompare();
  const { has: isFav, toggle: toggleFav } = useFavorites();
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [fpForm, setFpForm] = useState({ title: "", bhk: "", area: "" });
  const [fpFile, setFpFile] = useState(null);
  const [fpUploading, setFpUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/projects/${id}`);
        if (!cancelled) setProject(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading…</div>;
  if (!project) return <div className="py-32 text-center text-slate-500">Project not found.</div>;

  const checked = has(project.id);
  const fav = isFav(project.id);
  const images = project.images?.length ? project.images : ["https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2400"];
  const floorPlans = project.floor_plans || [];

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiry", { ...form, project_id: project.id, consent: true });
      toast.success("Inquiry sent. Our consultant will reach out shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error("Could not send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFav = async () => {
    const res = await toggleFav(project.id);
    if (res?.needsAuth) toast("Sign in to save favorites");
    else toast(fav ? "Removed from favorites" : "Saved to favorites");
  };

  const refreshProject = async () => {
    const r = await api.get(`/projects/${id}`);
    setProject(r.data);
  };

  const handleFpUpload = async (e) => {
    e.preventDefault();
    if (!fpFile) {
      toast.error("Pick a floor plan image first.");
      return;
    }
    setFpUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fpFile);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = up.data.url;
      const payload = {
        title: fpForm.title || null,
        bhk: fpForm.bhk ? parseInt(fpForm.bhk, 10) : null,
        area: fpForm.area ? parseInt(fpForm.area, 10) : null,
        image: imageUrl,
      };
      await api.post(`/projects/${id}/floor-plans`, payload);
      toast.success("Floor plan added.");
      setFpForm({ title: "", bhk: "", area: "" });
      setFpFile(null);
      await refreshProject();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setFpUploading(false);
    }
  };

  const handleFpDelete = async (fpId) => {
    if (!fpId) {
      toast.error("This floor plan is legacy data — re-add to enable deletion.");
      return;
    }
    try {
      await api.delete(`/projects/${id}/floor-plans/${fpId}`);
      toast.success("Floor plan removed.");
      await refreshProject();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="project-detail-page" className="pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
        <Link to="/projects" className="text-xs tracking-[0.2em] uppercase text-slate-600 hover:text-[#D4A437] inline-flex items-center gap-2" data-testid="back-to-listings">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Projects
        </Link>
      </div>

      {/* Image gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-8 grid md:grid-cols-12 gap-4">
        <div className="md:col-span-8 aspect-[16/10] bg-slate-50 overflow-hidden relative cursor-pointer group" onClick={() => setLightboxOpen(true)} data-testid="hero-image">
          <img src={images[activeImg]} alt={project.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/0 group-hover:bg-white/30 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity w-12 h-12 bg-[#D4A437] text-white flex items-center justify-center">
              <Maximize2 className="w-5 h-5" strokeWidth={1.5} />
            </div>
          </div>
        </div>
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
          {images.slice(0, 3).map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveImg(i)}
              className={`aspect-[4/3] overflow-hidden border ${activeImg === i ? "border-amber-500" : "border-slate-200"}`}
              data-testid={`thumb-${i}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </section>

      {lightboxOpen && (
        <Lightbox images={images} index={activeImg} onClose={() => setLightboxOpen(false)} onIndex={setActiveImg} />
      )}

      {/* Detail */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="overline mb-3 flex items-center gap-2">
            <MapPin className="w-3 h-3" /> {project.locality}, {project.city}
          </div>
          <div className="flex items-start justify-between gap-4 mb-2">
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 font-light">{project.name}</h1>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleFav}
                data-testid="detail-favorite-btn"
                className={`px-3 py-2 border text-xs tracking-wide flex items-center gap-2 transition-colors ${
                  fav ? "bg-[#D4A437] border-amber-600 text-white" : "border-slate-200 text-slate-800 hover:border-[#D4A437]"
                }`}
              >
                <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} />
                {fav ? "Saved" : "Save"}
              </button>
              <button
                onClick={() => (checked ? remove(project.id) : add(project))}
                data-testid="detail-compare-btn"
                className={`px-4 py-2 border text-xs tracking-wide flex items-center gap-2 transition-colors ${
                  checked ? "bg-[#D4A437] border-amber-600 text-white" : "border-slate-200 text-slate-800 hover:border-[#D4A437]"
                }`}
              >
                {checked ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                Compare
              </button>
            </div>
          </div>
          <p className="text-slate-600 mb-8">By <span className="text-slate-800">{project.developer}</span></p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100 my-10 border border-slate-100">
            <div className="bg-white p-5"><div className="overline mb-1">Starting</div><div className="font-serif text-2xl text-[#D4A437]">{formatPrice(project.price_min, project.currency)}</div></div>
            <div className="bg-white p-5"><div className="overline mb-1">Per Sqft</div><div className="font-serif text-2xl text-slate-900">{project.price_per_sqft?.toLocaleString()}</div></div>
            <div className="bg-white p-5"><div className="overline mb-1">BHK</div><div className="font-serif text-2xl text-slate-900">{project.bhk?.length ? project.bhk.join(", ") : "—"}</div></div>
            <div className="bg-white p-5"><div className="overline mb-1">Area</div><div className="font-serif text-2xl text-slate-900">{project.area_min}-{project.area_max}</div></div>
          </div>

          <div data-testid="price-disclaimer" className="-mt-6 mb-10 flex items-start gap-3 border-l-2 border-amber-200 pl-4 py-2">
            <ShieldCheck className="w-4 h-4 text-[#D4A437] mt-1 shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-slate-600 leading-relaxed font-light">
              <span className="text-slate-800">Indicative pricing.</span> Rates change with launch phase, floor, view and inventory.
              For the most current rate-card and unit-availability, please request a callback —
              our consultant will share the developer&apos;s latest cost-sheet directly.
              {project.rera && <> Project RERA: <span className="text-[#D4A437] font-mono">{project.rera}</span>.</>}
              {" "}OC Agent RERA: <span className="text-[#D4A437] font-mono">UPRERAAGT15084</span>.
            </p>
          </div>

          {/* Tabs */}
          <div className="border-t border-slate-100 pt-6">
            <div className="flex gap-1 border-b border-slate-200">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-5 py-3 text-xs tracking-[0.2em] uppercase border-b-2 transition-colors ${
                    tab === t.id ? "border-amber-500 text-[#D4A437]" : "border-transparent text-slate-600 hover:text-slate-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="py-8">
              {tab === "overview" && (
                <div data-testid="overview-content">
                  <h2 className="font-serif text-2xl text-slate-900 mb-4">About this project</h2>
                  <p className="text-slate-700 leading-relaxed font-light">{project.description}</p>
                  <div className="grid sm:grid-cols-3 gap-8 mt-12 border-t border-slate-100 pt-8">
                    <div>
                      <div className="overline mb-2">Possession</div>
                      <div className="text-slate-800 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4A437]" /> {project.possession || "Ready"}</div>
                    </div>
                    <div>
                      <div className="overline mb-2">RERA</div>
                      <div className="text-slate-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4A437]" /> {project.rera || "Verified"}</div>
                    </div>
                    <div>
                      <div className="overline mb-2">Status</div>
                      <div className="text-slate-800 capitalize">{project.status?.replace("_", " ")}</div>
                    </div>
                  </div>
                </div>
              )}

              {tab === "amenities" && (
                <div data-testid="amenities-content">
                  <h2 className="font-serif text-2xl text-slate-900 mb-6">What&apos;s included</h2>
                  {project.amenities?.length ? (
                    <div className="grid sm:grid-cols-3 gap-3">
                      {project.amenities.map((a) => (
                        <div key={a} className="flex items-center gap-2 text-sm text-slate-700 border border-slate-100 px-4 py-3">
                          <span className="w-1 h-1 bg-[#D4A437] rounded-full"></span> {a}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No amenities listed.</p>
                  )}
                </div>
              )}

              {tab === "floorplans" && (
                <div data-testid="floorplans-content">
                  <h2 className="font-serif text-2xl text-slate-900 mb-6">Floor Plans</h2>
                  {floorPlans.length ? (
                    <div className="grid sm:grid-cols-2 gap-6">
                      {floorPlans.map((fp, i) => (
                        <div key={fp.id || i} className="border border-slate-200 relative group">
                          <div className="aspect-[4/3] bg-slate-50">
                            {fp.image && <img src={fp.image} alt={fp.title} className="w-full h-full object-cover" />}
                          </div>
                          <div className="p-4">
                            <div className="font-serif text-lg text-slate-900">{fp.title || `${fp.bhk} BHK`}</div>
                            <div className="text-xs text-slate-500 mt-1">{fp.area} sqft · {fp.bhk} BHK</div>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => handleFpDelete(fp.id)}
                              data-testid={`fp-delete-${i}`}
                              className="absolute top-2 right-2 p-2 bg-white/80 border border-slate-200 hover:border-red-500 hover:text-red-500 text-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                              aria-label="Delete floor plan"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-slate-200 p-12 text-center">
                      <p className="text-slate-600 font-light">Floor plans available on request.</p>
                      <p className="text-xs text-slate-500 mt-2">Submit an inquiry on the right and our consultant will share detailed plans.</p>
                    </div>
                  )}

                  {isAdmin && (
                    <form
                      onSubmit={handleFpUpload}
                      data-testid="admin-fp-upload-form"
                      className="mt-10 border border-slate-200 p-6 bg-amber-50/40"
                    >
                      <div className="overline mb-4 flex items-center gap-2"><Upload className="w-3.5 h-3.5" /> Admin · Add a floor plan</div>
                      <div className="grid sm:grid-cols-4 gap-4">
                        <div className="sm:col-span-2">
                          <label className="text-xs text-slate-600 block mb-1">Title</label>
                          <input
                            value={fpForm.title}
                            onChange={(e) => setFpForm({ ...fpForm, title: e.target.value })}
                            placeholder="e.g. 3 BHK Premium"
                            data-testid="fp-input-title"
                            className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">BHK</label>
                          <input
                            type="number"
                            min="0"
                            value={fpForm.bhk}
                            onChange={(e) => setFpForm({ ...fpForm, bhk: e.target.value })}
                            data-testid="fp-input-bhk"
                            className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">Area (sqft)</label>
                          <input
                            type="number"
                            min="0"
                            value={fpForm.area}
                            onChange={(e) => setFpForm({ ...fpForm, area: e.target.value })}
                            data-testid="fp-input-area"
                            className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <label className="text-xs text-slate-600 block mb-1">Floor plan image (PNG/JPG, max 10 MB)</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setFpFile(e.target.files?.[0] || null)}
                            data-testid="fp-input-file"
                            className="w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#D4A437] file:text-white file:cursor-pointer hover:file:bg-[#D4A437]"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="submit"
                            disabled={fpUploading}
                            data-testid="fp-upload-btn"
                            className="w-full bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide py-2.5 transition-colors disabled:opacity-50"
                          >
                            {fpUploading ? "Uploading..." : "Upload"}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {tab === "location" && (
                <div data-testid="location-content">
                  <h2 className="font-serif text-2xl text-slate-900 mb-6">{project.locality}, {project.city}</h2>
                  {project.lat != null && project.lng != null ? (
                    <MapView
                      markers={[{
                        lat: project.lat,
                        lng: project.lng,
                        name: project.name,
                        locality: project.locality,
                        city: project.city,
                        price: formatPrice(project.price_min, project.currency),
                      }]}
                      center={{ lat: project.lat, lng: project.lng }}
                      zoom={14}
                      height={420}
                    />
                  ) : (
                    <div className="border border-slate-200 p-12 text-center text-slate-500">Location coordinates not available.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Inquiry */}
        <div className="lg:col-span-4">
          <form onSubmit={handleInquiry} data-testid="inquiry-form" className="lg:sticky lg:top-24 border border-slate-200 bg-amber-50/40 p-8">
            <div className="overline mb-2">Get in touch</div>
            <h3 className="font-serif text-2xl text-slate-900 mb-6">Schedule a private viewing</h3>
            <div className="space-y-3">
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" data-testid="inq-name"
                className="w-full bg-white border border-slate-100 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" data-testid="inq-email"
                className="w-full bg-white border border-slate-100 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
              <input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" data-testid="inq-phone"
                className="w-full bg-white border border-slate-100 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
              <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="I'd like to know more about…" rows={3} data-testid="inq-message"
                className="w-full bg-white border border-slate-100 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <ConsentCheckbox checked={consent} onChange={setConsent} testId="inq-consent-project" />
            <button type="submit" disabled={submitting || !consent} data-testid="inq-submit-btn"
              className="w-full mt-5 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium py-3 transition-colors disabled:opacity-60">
              {submitting ? "Sending..." : "Request Callback →"}
            </button>
            <a
              href={buildWhatsAppLink(`Hi OC Infraventures, I'm interested in ${project.name} (${project.locality}, ${project.city}). Please share details and a viewing slot.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="inq-whatsapp-btn"
              className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-3 transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
            <p className="text-xs text-slate-500 mt-4 text-center">A consultant will respond within 2 hours.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
